import { supabase } from "../supabaseClient";
import type { League, LeagueTeam, LeagueFixture, LeagueStanding } from "./leagues";

export async function listPublicLeagues(): Promise<League[]> {
  const { data, error } = await supabase
    .from("leagues")
    .select("*")
    .eq("is_public", true)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching public leagues:", error);
    return [];
  }

  return data || [];
}

export async function getPublicLeague(leagueId: string): Promise<League | null> {
  const { data, error } = await supabase
    .from("leagues")
    .select("*")
    .eq("id", leagueId)
    .eq("is_public", true)
    .maybeSingle();

  if (error) {
    console.error("Error fetching public league:", error);
    return null;
  }

  return data;
}

export async function getPublicLeagueTeams(leagueId: string): Promise<LeagueTeam[]> {
  const { data, error } = await supabase
    .from("league_teams")
    .select("*")
    .eq("league_id", leagueId)
    .order("team_name", { ascending: true });

  if (error) {
    console.error("Error fetching public league teams:", error);
    return [];
  }

  return data || [];
}

export async function getPublicLeagueFixtures(leagueId: string): Promise<LeagueFixture[]> {
  const { data, error } = await supabase
    .from("league_fixtures")
    .select("*")
    .eq("league_id", leagueId)
    .order("round_number", { ascending: true, nullsFirst: false })
    .order("fixture_order", { ascending: true, nullsFirst: false })
    .order("scheduled_at", { ascending: true, nullsFirst: false });

  if (error) {
    console.error("Error fetching public league fixtures:", error);
    return [];
  }

  return data || [];
}

export async function getPublicUpcomingFixtures(): Promise<(LeagueFixture & { league_name?: string })[]> {
  const { data: leagues, error: leaguesError } = await supabase
    .from("leagues")
    .select("id, name")
    .eq("is_public", true);

  if (leaguesError || !leagues || leagues.length === 0) return [];

  const leagueIds = leagues.map((l) => l.id);
  const leagueMap = new Map(leagues.map((l) => [l.id, l.name]));

  const { data, error } = await supabase
    .from("league_fixtures")
    .select("*")
    .in("league_id", leagueIds)
    .in("status", ["upcoming", "SCHEDULED", "scheduled"])
    .order("scheduled_at", { ascending: true, nullsFirst: true })
    .order("match_date", { ascending: true, nullsFirst: true });

  if (error) {
    console.error("Error fetching public upcoming fixtures:", error);
    return [];
  }

  return (data || []).map((f) => ({ ...f, league_name: leagueMap.get(f.league_id) }));
}

export async function getPublicLeagueStandings(leagueId: string): Promise<LeagueStanding[]> {
  const { data, error } = await supabase
    .from("league_standings")
    .select("*")
    .eq("league_id", leagueId)
    .order("points", { ascending: false })
    .order("net_run_rate", { ascending: false });

  if (error) {
    console.error("Error fetching public league standings:", error);
    return [];
  }

  return data || [];
}
